import { AuthToken } from '../../enums';
import * as authenticationContext from '../authentication-context';

const CHANNEL_NAME = 'cmx-auth-token-request';

type Authorization = {
  token: string | null;
  contextId: number | null | undefined;
  expiryTimeToLive: string | number | null;
  /** Redux ui.context state from the donor tab — lets the recipient set the
   *  active context without a PUT /contexts/v1 token-rotation call. */
  context?: unknown;
  /** Redux ui.activeUser from the donor tab — populates the avatar / user info
   *  that fetchContexts normally sets via setActiveUser() when it short-circuits. */
  activeUser?: unknown;
};

type TokenRequestMessage = {
  type: typeof AuthToken.REQUEST;
  requestId: string;
};

type TokenResponseMessage = {
  type: typeof AuthToken.RESPONSE;
  requestId: string;
  authorization: Authorization;
};

let requestCounter = 0;

export class TokenRequestChannel {
  private channel: BroadcastChannel | null = null;
  private store: { getState: () => unknown } | null = null;

  /** Provide the Redux store so the listener can include context state in responses. */
  setStore(store: { getState: () => unknown }): void {
    this.store = store;
  }

  /** Start listening for TOKEN_REQUEST messages from peer tabs. */
  startListening(): void {
    if (this.channel) {
      return; // already listening
    }
    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return; // BroadcastChannel not supported
    }
    this.channel.onmessage = (event: MessageEvent) => {
      const data = event.data as TokenRequestMessage;
      if (data?.type !== AuthToken.REQUEST) {
        return;
      }
      const token = authenticationContext.getToken();
      if (!token) {
        return; // not authenticated — don't respond
      }
      const auth = authenticationContext.getAuthorization();
      const state = this.store?.getState() as any;
      const response: TokenResponseMessage = {
        type: AuthToken.RESPONSE,
        requestId: data.requestId,
        authorization: {
          token: auth.token,
          contextId: auth.contextId,
          expiryTimeToLive: auth.expiryTimeToLive,
          context: state?.ui?.context ?? undefined,
          activeUser: state?.ui?.activeUser ?? undefined
        }
      };
      this.channel?.postMessage(response);
    };
  }

  /** Stop listening and close the channel. */
  stopListening(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }

  /**
   * Broadcast a TOKEN_REQUEST and wait for a TOKEN_RESPONSE from an
   * authenticated peer tab. Resolves with the authorization object or
   * null if no response within the timeout.
   */
  static request(timeoutMs: number = 2000): Promise<Authorization | null> {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      return Promise.resolve(null); // BroadcastChannel not supported
    }

    const requestId = `req-${++requestCounter}-${Date.now()}`;

    return new Promise<Authorization | null>(resolve => {
      let settled = false;

      const cleanup = () => {
        if (!settled) {
          settled = true;
          channel.onmessage = null;
          channel.close();
        }
      };

      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);

      channel.onmessage = (event: MessageEvent) => {
        const data = event.data as TokenResponseMessage;
        if (
          data?.type !== AuthToken.RESPONSE ||
          data.requestId !== requestId ||
          !data.authorization?.token
        ) {
          return; // not our response, or empty token
        }
        clearTimeout(timer);
        cleanup();
        resolve(data.authorization);
      };

      const request: TokenRequestMessage = {
        type: AuthToken.REQUEST,
        requestId
      };
      channel.postMessage(request);
    });
  }
}
